import when from 'when'
import Logger from 'libraries/logger'
import { authUrl } from 'config/backend'

/*
 * Wraps a Backbone object in a promise that fetches it at most once.
 *
 * The promise gates whether a module counts as loaded, so it must never be
 * left pending: that is what made the newsroom module unclickable with no
 * error anywhere (LS-3892). It must also not give up on the first failure —
 * the user should not see a broken module for something a second request
 * fixes. A failed fetch is therefore
 *
 *   - retried with a short backoff, bypassing the browser cache, or
 *   - answered with the login form when the server says the session is gone
 *     (and rejected as well, so no consumer waits on a dead session while
 *     the redirect is under way), or
 *   - rejected outright once neither of those applies.
 *
 * The `fetched` latch stays set on failure, so nothing refetches behind the
 * back of a consumer that has already been told the load failed.
 */

// Delays before retry 1 and retry 2. Short enough that a user waiting for a
// module does not notice, long enough to outlast a blip.
var RETRY_DELAYS = [500, 2000];

// The server told us the session is not valid. Retrying sends the same dead
// credentials again, so the only way back is authenticating anew.
var SESSION_GONE_STATUSES = [401, 403];

var redirectedToLogin = false;

function sessionIsGone(xhr) {
  return SESSION_GONE_STATUSES.indexOf(xhr && xhr.status) !== -1;
}

function goToLoginForm() {
  // Once per page load: /login sends a still-authenticated user straight back
  // into the app, so an unguarded redirect could bounce.
  if (redirectedToLogin) {
    return;
  }
  redirectedToLogin = true;
  // The webapp domain has no login route — authentication lives on the
  // backend origin. Same pattern as the boot-time check in login.js.
  window.location.href = authUrl('/login', { redirect: window.location.href });
}

function fetchOncePromise(object) {
  var deferred = when.defer();
  var promise = deferred.promise;

  function attempt(attemptsMade) {
    // The first try may be answered from the browser cache; a retry must not
    // be, because a stale cached response is one of the ways this fails and
    // repeating the identical request would repeat it.
    var options = attemptsMade === 0 ? {} : { cache: false };

    object.fetch(options)
      .done(function () {
        deferred.resolve(object);
      })
      .fail(function (xhr) {
        var status = xhr && xhr.status;

        if (sessionIsGone(xhr)) {
          Logger.error('fetchOncePromise: session gone (status', status,
                       '), sending the user to the login form');
          goToLoginForm();
          deferred.reject(new Error('session gone (status ' + status + ')'));
          return;
        }

        if (attemptsMade < RETRY_DELAYS.length) {
          var delay = RETRY_DELAYS[attemptsMade];
          Logger.warn('fetchOncePromise: fetch failed with status', status,
                      '- retrying in', delay, 'ms');
          setTimeout(function () {
            attempt(attemptsMade + 1);
          }, delay);
          return;
        }

        Logger.error('fetchOncePromise: fetch failed with status', status,
                     'after', RETRY_DELAYS.length, 'retries');
        deferred.reject(new Error('fetch failed with status ' + status));
      });
  }

  var fetched = false;
  promise.fetch = function () {
    if (!fetched) {
      fetched = true;
      attempt(0);
    }
    return promise;
  };

  return promise;
}

export default fetchOncePromise;
